import * as z from "zod";
import { FedexPinDeliveryOption } from "../fulfillments/fedex-customer-registration";

export enum EasypostFedexMfaMode {
  CREATE = "create",
  UPDATE = "update",
}

const addressValidationSchema = z.object({
  accountNumber: z.string(),
  accountName: z.string(),
  street1: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  countryCode: z.string(),
  hubId: z.union([z.string(), z.number()]).optional(),
});

// Address validation (step 1 - required for all flows)
export const easypostFedexMfaAddressValidationInputSchema =
  z.discriminatedUnion("mode", [
    addressValidationSchema.extend({
      mode: z.literal(EasypostFedexMfaMode.CREATE),
      easypostCarrierType: z.string(),
      merchantFacingName: z.string(),
    }),
    addressValidationSchema.extend({
      mode: z.literal(EasypostFedexMfaMode.UPDATE),
      carrierAccountId: z.string(),
      easypostCarrierType: z.string(),
    }),
  ]);

export type EasypostFedexMfaAddressValidationInput = z.infer<
  typeof easypostFedexMfaAddressValidationInputSchema
>;

export const easypostFedexMfaAddressValidationOutputSchema = z.object({
  complete: z.boolean(),
  mfaOptions: z
    .object({
      options: z.array(z.enum(["SMS", "CALL", "EMAIL", "INVOICE"])),
      phoneNumber: z.string().nullable(),
      emailAddress: z.string().nullable(),
    })
    .optional(),
  error: z.string().optional(),
});

export type EasypostFedexMfaAddressValidationOutput = z.infer<
  typeof easypostFedexMfaAddressValidationOutputSchema
>;

// PIN generation (step 2 for SMS/CALL/EMAIL flow)
const pinGenerationBaseSchema = z.object({
  accountNumber: z.string(),
  deliveryOption: z.nativeEnum(FedexPinDeliveryOption),
  easypostCarrierType: z.string(),
  hubId: z.union([z.string(), z.number()]).optional(),
});

export const easypostFedexMfaGeneratePinInputSchema = z.discriminatedUnion(
  "mode",
  [
    pinGenerationBaseSchema.extend({
      mode: z.literal(EasypostFedexMfaMode.CREATE),
    }),
    pinGenerationBaseSchema.extend({
      mode: z.literal(EasypostFedexMfaMode.UPDATE),
      carrierAccountId: z.string(),
    }),
  ],
);

export type EasypostFedexMfaGeneratePinInput = z.infer<
  typeof easypostFedexMfaGeneratePinInputSchema
>;

export const easypostFedexMfaGeneratePinOutputSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

export type EasypostFedexMfaGeneratePinOutput = z.infer<
  typeof easypostFedexMfaGeneratePinOutputSchema
>;

// PIN validation (step 3 for SMS/CALL/EMAIL flow)
const validatePinBaseSchema = z.object({
  accountNumber: z.string(),
  accountName: z.string(),
  pin: z.string(),
  easypostCarrierType: z.string(),
  hubId: z.union([z.string(), z.number()]).optional(),
});

export const easypostFedexMfaValidatePinInputSchema = z.discriminatedUnion(
  "mode",
  [
    validatePinBaseSchema.extend({
      mode: z.literal(EasypostFedexMfaMode.CREATE),
      merchantFacingName: z.string(),
    }),
    validatePinBaseSchema.extend({
      mode: z.literal(EasypostFedexMfaMode.UPDATE),
      carrierAccountId: z.string(),
    }),
  ],
);

export type EasypostFedexMfaValidatePinInput = z.infer<
  typeof easypostFedexMfaValidatePinInputSchema
>;

export const easypostFedexMfaValidatePinOutputSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

export type EasypostFedexMfaValidatePinOutput = z.infer<
  typeof easypostFedexMfaValidatePinOutputSchema
>;

// Invoice validation (step 2+final for invoice flow)
const validateInvoiceBaseSchema = z.object({
  accountNumber: z.string(),
  accountName: z.string(),
  invoiceNumber: z.string(),
  invoiceDate: z.string(),
  invoiceAmount: z.string(),
  invoiceCurrency: z.string(),
  easypostCarrierType: z.string(),
  hubId: z.union([z.string(), z.number()]).optional(),
});

export const easypostFedexMfaValidateInvoiceInputSchema = z.discriminatedUnion(
  "mode",
  [
    validateInvoiceBaseSchema.extend({
      mode: z.literal(EasypostFedexMfaMode.CREATE),
      merchantFacingName: z.string(),
    }),
    validateInvoiceBaseSchema.extend({
      mode: z.literal(EasypostFedexMfaMode.UPDATE),
      carrierAccountId: z.string(),
    }),
  ],
);

export type EasypostFedexMfaValidateInvoiceInput = z.infer<
  typeof easypostFedexMfaValidateInvoiceInputSchema
>;

export const easypostFedexMfaValidateInvoiceOutputSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

export type EasypostFedexMfaValidateInvoiceOutput = z.infer<
  typeof easypostFedexMfaValidateInvoiceOutputSchema
>;
